import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DeletionProofCard = ({ token }) => {
  const [id, setId] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [rep, setRep] = useState(null);
  const run = async () => {
    if (!id.trim()) { toast.error('Enter the account id (uuid)'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/deletion-proof`, { account_id: id.trim(), email: email ? email.trim() : null }, { headers: { Authorization: `Bearer ${token}` } });
      setRep(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Check failed'); }
    finally { setBusy(false); }
  };
  const tone = rep?.verdict === 'CLEAN' ? 'border-green-300 bg-green-50 text-green-800' : rep?.verdict === 'HELD' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-red-300 bg-red-50 text-red-800';
  const held = rep?.held_pending_legal_advice || [];
  return (
    <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="deletion-proof-card">
      <h3 className="font-bold text-lg mb-1">Deletion proof (read-only)</h3>
      <p className="text-xs text-muted-foreground mb-3">For one account id: every collection, Cloudinary (photos + backups) and the app log. Expected = audit row without personal data. Nothing is written.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <input className="rounded-md border px-2 py-1 text-sm font-mono flex-1 min-w-[280px]" placeholder="account id (uuid)" value={id} onChange={(e) => setId(e.target.value)} data-testid="deletion-proof-id" />
        <input className="rounded-md border px-2 py-1 text-sm flex-1 min-w-[220px]" placeholder="address (optional, widens the search)" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="deletion-proof-email" />
        <Button size="sm" className="rounded-full" onClick={run} disabled={busy} data-testid="deletion-proof-run">{busy ? 'Checking…' : 'Check'}</Button>
      </div>
      {rep && (
        <div className="mt-3 space-y-2 text-sm">
          <p className={`rounded-lg border px-3 py-2 font-semibold ${tone}`} data-testid="deletion-proof-verdict">
            {rep.verdict}{rep.verdict === 'HELD' ? ' (pending legal advice)' : ''} — account {rep.account_id_prefix} · {rep.left_behind.length} left behind · {held.length} held pending legal advice · {rep.expected_audit_rows.length} expected audit rows
          </p>
          {rep.left_behind.length > 0 && (
            <ul className="list-disc pl-5" data-testid="deletion-proof-left-behind">
              {rep.left_behind.map((r, i) => <li key={i}><span className="font-mono">{r.collection}</span> {r.doc_id} — {r.why}</li>)}
            </ul>
          )}
          {held.length > 0 && (
            <ul className="list-disc pl-5 text-amber-800" data-testid="deletion-proof-held">
              {held.map((r, i) => <li key={i}><span className="font-mono">{r.collection}</span> {r.doc_id} · {String(r.at || '').slice(0, 19)} — {r.why}</li>)}
            </ul>
          )}
          {rep.expected_audit_rows.length > 0 && (
            <details><summary className="cursor-pointer text-xs">Expected audit rows ({rep.expected_audit_rows.length})</summary>
              <ul className="list-disc pl-5 text-xs mt-1">{rep.expected_audit_rows.map((r, i) => <li key={i}><span className="font-mono">{r.collection}</span> {r.doc_id} · {r.type || '—'} · {String(r.at || '').slice(0, 19)} — {r.why}</li>)}</ul>
            </details>
          )}
          <p className="text-xs">Cloudinary: {Object.entries(rep.cloudinary).map(([k, v]) => `${k} ${v.assets ?? 'n/a'} (${v.verdict})`).join(' · ')}</p>
          <p className="text-xs">App log: {rep.app_log.readable ? `${rep.app_log.lines_mentioning_id_prefix} line(s) mention the id prefix — ${rep.app_log.note}` : rep.app_log.note}</p>
          <p className="text-[11px] text-muted-foreground">Compares: {rep.compares}</p>
        </div>
      )}
    </div>
  );
};
